import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Upload as UploadIcon,
  Home as HomeIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import ImportWizard from '../components/import/ImportWizard';
import { importApi } from '../services/api';
import ResponsiveHeader from '../components/ResponsiveHeader';
import MobileCardList from '../components/MobileCardList';

// Mappa per label entità
const entityLabels = {
  customers: 'Clienti',
  invoices: 'Fatture',
  meters: 'Contatori',
  plants: 'Impianti',
  contracts: 'Contratti',
  advances: 'Acconti',
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`import-tabpanel-${index}`}
      aria-labelledby={`import-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ImportPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState(null);

  // Carica logs quando si passa alla tab storico
  useEffect(() => {
    if (tabValue === 1) {
      loadLogs();
    }
  }, [tabValue]);

  const loadLogs = async () => {
    setLoadingLogs(true);
    setLogsError(null);
    try {
      const response = await importApi.getLogs({ limit: 50 });
      setLogs(response.data.data.logs || []);
    } catch (err) {
      setLogsError('Errore nel caricamento dello storico');
      console.error(err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleComplete = () => {
    // Ricarica logs e torna alla dashboard
    loadLogs();
    navigate('/crm');
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 3 }, width: '100%' }}>
      {/* Breadcrumbs */}
      {!isMobile && <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/crm"
          sx={{ display: 'flex', alignItems: 'center' }}
          underline="hover"
          color="inherit"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <UploadIcon sx={{ mr: 0.5 }} fontSize="small" />
          Importa Dati
        </Typography>
      </Breadcrumbs>}

      {/* Header */}
      <ResponsiveHeader
        title="Importazione Dati"
        subtitle="Importa clienti, fatture, contatori e impianti da file CSV oppure reimporta file esportati dal gestionale."
      >
        <UploadIcon sx={{ fontSize: 24, color: 'primary.main' }} />
      </ResponsiveHeader>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<UploadIcon />} 
            iconPosition="start" 
            label="Nuovo Import" 
          />
          <Tab 
            icon={<HistoryIcon />} 
            iconPosition="start" 
            label="Storico Import" 
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <ImportWizard onComplete={handleComplete} onCancel={handleCancel} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Storico delle importazioni
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Visualizza tutte le operazioni di import effettuate, con dettagli su record inseriti, aggiornati e errori.
          </Typography>

          {loadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logsError ? (
            <Alert severity="error">{logsError}</Alert>
          ) : logs.length === 0 ? (
            <Alert severity="info">
              Nessuna importazione effettuata. Usa la tab "Nuovo Import" per importare dati.
            </Alert>
          ) : isMobile ? (
            <MobileCardList
              rows={logs}
              emptyMessage="Nessuna importazione effettuata. Usa la tab Nuovo Import per iniziare."
              renderCard={(log) => (
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mb: 0.75 }}>
                          <Chip
                            label={entityLabels[log.entity] || log.entity}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={log.import_source === 'exported' ? 'Reimport' : 'Manuale'}
                            size="small"
                            color={log.import_source === 'exported' ? 'success' : 'default'}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(log.completed_at || log.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
                          File: {log.file_name || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap>
                          Utente: {log.user_name || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                        <Chip label={`+${log.inserted}`} size="small" color="success" />
                        <Chip label={`~${log.updated}`} size="small" color="info" />
                        <Chip label={`!${log.error_count}`} size="small" color={log.error_count > 0 ? 'error' : 'default'} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Utente</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Sorgente</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>File</TableCell>
                    <TableCell align="right">Inseriti</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Aggiornati</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Saltati</TableCell>
                    <TableCell align="right">Errori</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{formatDate(log.completed_at || log.created_at)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{log.user_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={entityLabels[log.entity] || log.entity}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip 
                          label={log.import_source === 'exported' ? 'Reimport' : 'Manuale'}
                          size="small"
                          color={log.import_source === 'exported' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Tooltip title={log.file_name || '-'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {log.file_name || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={log.inserted} 
                          size="small" 
                          color="success" 
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip 
                          label={log.updated} 
                          size="small" 
                          color="info" 
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip 
                          label={log.skipped} 
                          size="small" 
                          color="warning" 
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={log.error_count} 
                          size="small" 
                          color={log.error_count > 0 ? 'error' : 'default'}
                          variant="filled"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
}
